import { pct } from '../../lib/numbers';
import { daysSince, rule } from '../../lib/rules';
import type { Shipment } from '@serb/shared';
import { useShipments } from '../shipments/useShipments';
import type { Agent, Rfq } from './sample';
import { useRfqs } from './useAgents';

/**
 * Transit reliability: of the shipments this agent delivered, the share that arrived by the promised date.
 * `null` until the vault says how any delivery went.
 */
function transitReliability(rfqs: Rfq[]): number | null {
  const delivered = rfqs.filter((r) => r.delivered);
  return delivered.length ? pct(delivered.filter((r) => r.delivered === 'On time').length, delivered.length) : null;
}

/**
 * Price competitiveness: on each request where another agent also gave a price (same shipment, same currency),
 * the lowest price divided by this agent's price (100% = the cheapest). The average of those. `null` when there is nothing to compare.
 */
function priceCompetitiveness(rfqs: Rfq[], all: Rfq[]): number | null {
  const scores = rfqs.flatMap((r) => {
    if (r.rate === null) return [];
    const others = all.filter(
      (o) => o.srb === r.srb && o.currency === r.currency && o.agentId !== r.agentId && o.rate !== null,
    );
    if (!others.length) return [];
    return [Math.min(r.rate, ...others.map((o) => o.rate as number)) / r.rate];
  });
  return scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) : null;
}

const CLOSED = ['Lost', 'Cancelled', 'Merged'];
const DELIVERED = ['Arrived', 'Completed'];

/** What an agent gave us, counted from the recorded rates (`all`) and our shipments */
export function statsFrom(a: Agent, all: Rfq[], allShips: Shipment[]) {
  const rfqs = all.filter((r) => r.agentId === a.id);
  const count = (o: string) => rfqs.filter((r) => r.outcome === o).length;
  const total = rfqs.length;
  const won = count('Selected');
  const lost = count('Not Selected');
  const pending = count('Pending');
  const notQuoted = count('No Quote');
  const received = rfqs.filter((r) => r.rate !== null).length;
  // still waiting for an answer for longer than the rule book's chase time
  const toChase = rfqs.filter(
    (r) => r.rate === null && r.outcome === 'Pending' && daysSince(r.date) >= rule('chaseAgentDays'),
  ).length;
  const ships = allShips.filter((s) => s.agent.toLowerCase() === a.name.toLowerCase());
  return {
    rfqs,
    total,
    received,
    won,
    lost,
    pending,
    notQuoted,
    responseRate: pct(received, total),
    transitReliability: transitReliability(rfqs),
    priceCompetitiveness: priceCompetitiveness(rfqs, all),
    winRate: pct(won, total),
    ships,
    active: ships.filter((s) => !DELIVERED.includes(s.stage) && !CLOSED.includes(s.stage)).length,
    completed: ships.filter((s) => DELIVERED.includes(s.stage)).length,
    lastActivity: [...rfqs].sort((x, y) => y.date.localeCompare(x.date))[0]?.date ?? '',
    // needs attention: it did not answer a request for rates
    toChase,
    needsAttention: notQuoted > 0 || toChase > 0,
  };
}
export type AgentStats = ReturnType<typeof statsFrom>;

/** For the agent pages: counted from the real quotes and shipments in the vault */
export function useAgentStats() {
  const rfqs = useRfqs();
  const { items } = useShipments();
  return (a: Agent) => statsFrom(a, rfqs, items);
}
